export interface KenBurnsConfig {
  startScale: number
  endScale: number
  startX?: number
  startY?: number
  endX?: number
  endY?: number
}

export interface MotionPreset {
  id: string
  name: string
  category: 'zoom' | 'pan' | 'combined'
  description: string
  kenBurns: KenBurnsConfig
}

export const MOTION_PRESETS: MotionPreset[] = [
  // Zoom presets
  {
    id: 'slow_zoom_in',
    name: 'Slow Zoom In',
    category: 'zoom',
    description: 'Subtle push-in',
    kenBurns: { startScale: 1.0, endScale: 1.15 },
  },
  {
    id: 'zoom_in',
    name: 'Zoom In',
    category: 'zoom',
    description: 'Standard zoom in',
    kenBurns: { startScale: 1.0, endScale: 1.3 },
  },
  {
    id: 'dramatic_zoom_in',
    name: 'Dramatic Zoom',
    category: 'zoom',
    description: 'Intense push-in',
    kenBurns: { startScale: 1.0, endScale: 1.5 },
  },
  {
    id: 'slow_zoom_out',
    name: 'Slow Zoom Out',
    category: 'zoom',
    description: 'Subtle reveal',
    kenBurns: { startScale: 1.15, endScale: 1.0 },
  },
  {
    id: 'zoom_out',
    name: 'Zoom Out',
    category: 'zoom',
    description: 'Wide reveal',
    kenBurns: { startScale: 1.3, endScale: 1.0 },
  },

  // Pan presets
  {
    id: 'pan_left',
    name: 'Pan Left',
    category: 'pan',
    description: 'Drift leftward',
    kenBurns: { startScale: 1.1, endScale: 1.1, startX: 0.08, endX: -0.08 },
  },
  {
    id: 'pan_right',
    name: 'Pan Right',
    category: 'pan',
    description: 'Drift rightward',
    kenBurns: { startScale: 1.1, endScale: 1.1, startX: -0.08, endX: 0.08 },
  },
  {
    id: 'pan_up',
    name: 'Pan Up',
    category: 'pan',
    description: 'Drift upward',
    kenBurns: { startScale: 1.1, endScale: 1.1, startY: 0.06, endY: -0.06 },
  },
  {
    id: 'pan_down',
    name: 'Pan Down',
    category: 'pan',
    description: 'Drift downward',
    kenBurns: { startScale: 1.1, endScale: 1.1, startY: -0.06, endY: 0.06 },
  },

  // Combined presets
  {
    id: 'ken_burns_classic',
    name: 'Ken Burns Classic',
    category: 'combined',
    description: 'Zoom + drift',
    kenBurns: { startScale: 1.0, endScale: 1.2, startX: -0.05, endX: 0.05 },
  },
  {
    id: 'dramatic_push',
    name: 'Dramatic Push',
    category: 'combined',
    description: 'Strong centered zoom',
    kenBurns: { startScale: 1.0, endScale: 1.4 },
  },
  {
    id: 'reveal_pan',
    name: 'Reveal Pan',
    category: 'combined',
    description: 'Zoom out with drift',
    kenBurns: { startScale: 1.3, endScale: 1.0, startX: 0.08, endX: -0.08 },
  },
  {
    id: 'float',
    name: 'Float',
    category: 'combined',
    description: 'Gentle drift and zoom',
    kenBurns: { startScale: 1.05, endScale: 1.1, startX: -0.02, startY: -0.02, endX: 0.02, endY: 0.02 },
  },
]

export const MOTION_PRESET_MAP = new Map(MOTION_PRESETS.map(p => [p.id, p]))

export function getPresetById(id: string): MotionPreset | undefined {
  return MOTION_PRESET_MAP.get(id)
}

export function matchPreset(kb: KenBurnsConfig): MotionPreset | undefined {
  return MOTION_PRESETS.find(p => {
    const pk = p.kenBurns
    return Math.abs(pk.startScale - kb.startScale) < 0.01
      && Math.abs(pk.endScale - kb.endScale) < 0.01
      && Math.abs((pk.startX ?? 0) - (kb.startX ?? 0)) < 0.01
      && Math.abs((pk.startY ?? 0) - (kb.startY ?? 0)) < 0.01
      && Math.abs((pk.endX ?? 0) - (kb.endX ?? 0)) < 0.01
      && Math.abs((pk.endY ?? 0) - (kb.endY ?? 0)) < 0.01
  })
}

export const SPEED_PRESETS = [0.25, 0.5, 1, 1.5, 2, 4] as const
